import { useMemo } from 'react';
import {
    type AeroOptimizationJobInputState,
    parseManualPartialExits,
    parseProfitFloors
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';

interface ExitPlanPreviewProps {
    state: AeroOptimizationJobInputState;
}

type RungTone = 'target' | 'partial' | 'entry' | 'floor' | 'stop';

interface Rung {
    tone: RungTone;
    level: number;
    priority: number;
    value: string;
    label: string;
    note: string | null;
}

const TONE_STYLES: Record<RungTone, { dot: string; value: string }> = {
    target: { dot: 'bg-green-400', value: 'text-green-300' },
    partial: { dot: 'bg-cyan-400', value: 'text-cyan-300' },
    entry: { dot: 'bg-zinc-300 ring-2 ring-zinc-500', value: 'text-zinc-200' },
    floor: { dot: 'bg-amber-400', value: 'text-amber-300' },
    stop: { dot: 'bg-red-400', value: 'text-red-300' }
};

const median = (values: number[]): number | null => {
    if (values.length === 0) return null;

    const sorted = [...values].sort((left, right) => left - right);

    return sorted[Math.floor(sorted.length / 2)] ?? null;
};

const formatSigned = (value: number): string => {
    const rounded = Math.round(value * 100) / 100;

    return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

const buildRungs = (state: AeroOptimizationJobInputState): Rung[] => {
    const rungs: Rung[] = [];

    const profitTarget = state.profitTargetEnabled ? median(state.profitTargetPercentages) : null;

    if (profitTarget !== null) {
        rungs.push({
            tone: 'target',
            level: profitTarget,
            priority: 3,
            value: formatSigned(profitTarget),
            label: 'Profit target',
            note: null
        });
    }

    if (state.profitTargetEnabled && state.partialExitMode === 'manual') {
        for (const pair of parseManualPartialExits(state.manualPartialExits).pairs) {
            rungs.push({
                tone: 'partial',
                level: pair.atProfit,
                priority: 2,
                value: formatSigned(pair.atProfit),
                label: `Sell ${pair.exitPercentage}% of the position`,
                note: null
            });
        }
    }

    if (state.profitTargetEnabled && state.partialExitMode === 'auto') {
        const from = Number(state.autoPartialExits.atProfitFrom);
        const to = Number(state.autoPartialExits.atProfitTo);
        const hasRange = Number.isFinite(from) && Number.isFinite(to);
        const level = hasRange ? (from + to) / 2 : (profitTarget ?? 10) / 2;

        rungs.push({
            tone: 'partial',
            level,
            priority: 2,
            value: hasRange ? `${formatSigned(from)}–${formatSigned(to)}` : 'auto',
            label: 'Auto partial-exit ladders',
            note: 'AERO generates and tests partial-exit combinations across this range'
        });
    }

    for (const floor of parseProfitFloors(state.profitFloors).floors) {
        rungs.push({
            tone: 'floor',
            level: floor.triggerPercentage,
            priority: 1,
            value: formatSigned(floor.triggerPercentage),
            label: 'Profit floor armed',
            note: `Exit if profit falls back to ${formatSigned(floor.sellAtPercentage)}`
        });
    }

    rungs.push({ tone: 'entry', level: 0, priority: 0, value: '0%', label: 'Trade entry', note: null });

    const stopLoss = state.stopLossEnabled ? median(state.stopLossPercentages) : null;

    if (stopLoss !== null) {
        const trailBy = state.trailingStopEnabled ? median(state.trailingByPercentages) : null;
        const trailActivation = state.trailingStopEnabled ? median(state.trailingActivationProfits) : null;

        rungs.push({
            tone: 'stop',
            level: -stopLoss,
            priority: -1,
            value: formatSigned(-stopLoss),
            label: 'Stop loss',
            note:
                trailBy !== null && trailActivation !== null
                    ? `Trails ${trailBy}% once the trade is up ${formatSigned(trailActivation)}`
                    : state.stopLossBasis === 'requiredCapital'
                      ? 'Measured against required capital'
                      : 'Measured against opening premium'
        });
    }

    return rungs.sort((left, right) => right.level - left.level || right.priority - left.priority);
};

const ExitPlanPreview = ({ state }: ExitPlanPreviewProps) => {
    const rungs = useMemo(() => buildRungs(state), [state]);
    const hasExitRung = rungs.some((rung) => rung.tone !== 'entry');

    if (!hasExitRung) {
        return <div className="text-sm text-zinc-500">Configure an exit type to preview the trade plan.</div>;
    }

    return (
        <div>
            <div className="relative pl-1">
                <div className="absolute bottom-2 left-[6px] top-2 w-px bg-zinc-700" />
                <div className="space-y-3">
                    {rungs.map((rung, index) => (
                        <div className="relative flex items-start gap-3" key={`${rung.tone}-${rung.level}-${index}`}>
                            <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${TONE_STYLES[rung.tone].dot}`} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2">
                                    <span className={`font-mono text-sm font-semibold ${TONE_STYLES[rung.tone].value}`}>{rung.value}</span>
                                    <span className="text-sm text-zinc-300">{rung.label}</span>
                                </div>
                                {rung.note && <div className="text-xs text-zinc-500">{rung.note}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-3 text-xs text-zinc-500">Representative plan using the midpoint of each value set.</div>
        </div>
    );
};

export default ExitPlanPreview;
